import { AccessTime, PaymentsOutlined } from '@mui/icons-material'
import { createElement, useContext } from 'react'
import dayjs from 'dayjs'

// others
import { yupResolver } from '@hookform/resolvers/yup'
import { Dialog, DialogContent, DialogTitle } from '@mui/material'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import {
  useCreateManualEntryMutation,
  useCreateNoteMutation,
  useNoteTypesQuery,
  UserRole,
  useUpdateEntryMutation
} from '../../../generated'
import { InvestigatorLogo } from '../../assets/svgs'
import { AppContext, AuthContext } from '../../context'
import useUsers from '../../hooks/useUsers'
import { ManualTimeEntryForm } from '../../interfaceTypes/forms'
import { ManualEntryProps } from '../../interfaceTypes/props'
import { manualEntrySchema } from '../../validationSchema'

// components
import { useTimer } from '../../context/TimerContext'
import { Alert } from '../common/Alert'
import CommonEntryFields from './CommonEntryFields'
import { decimalHoursToHHMM, titleize } from '../../utils'
import { TIME_FORMAT_HH_MM_REGEX, TIME_FORMAT_HH_REGEX } from '../../constants'

const ManualEntry = ({
  isOpen,
  handleClose,
  defaultValues,
  time,
  title = 'Add Entry',
  refetch,
  initialCase,
  lockCase
}: ManualEntryProps) => {
  const { currentUser } = useContext(AuthContext)
  const { allUsers } = useUsers({})

  const { startTimer } = useTimer()

  const { data: noteTypesData } = useNoteTypesQuery({ fetchPolicy: 'network-only' })
  const staffNoteTypeId = noteTypesData?.fetchAllNoteTypes?.noteTypes?.find(nt => nt?.noteType === 'staff')?.id

  const [createNote] = useCreateNoteMutation()

  const {
    isExpenseEntry,
    isCostEntry,
    isLegalFee,
    entryType: pageEntryType,
    isTimeEntry,
    isNonClientEntry
  } = useContext(AppContext)

  const methods = useForm<ManualTimeEntryForm>({
    resolver: yupResolver(manualEntrySchema, { context: { isExpenseEntry, isLegalFee } }),
    context: { isNonClientEntry },
    mode: 'onChange',
    defaultValues: {
      amount: defaultValues?.amount ?? (isExpenseEntry ? 0 : undefined),
      qty: isExpenseEntry ? defaultValues?.qty ?? 1 : undefined,
      unitPrice: defaultValues?.unitPrice ?? undefined,
      entryId: defaultValues?.entryId ?? '',
      caseId: defaultValues?.caseId ?? '',
      caseNumber: defaultValues?.caseNumber,
      duration: isExpenseEntry ? undefined : time ?? '',
      description: defaultValues?.description ?? '',
      date: defaultValues?.date ?? new Date(),
      billable: defaultValues?.billable ?? true,
      showOnBill: defaultValues?.showOnBill ?? true,
      sendNotesToMatterNotes: defaultValues?.sendNotesToMatterNotes ?? false,
      entryType: defaultValues?.entryType,
      rate: defaultValues?.rate ?? 0,
      rateType: defaultValues?.rateType ?? undefined,
      creatorId: currentUser?.id ?? '',
      userId: defaultValues?.userId ?? currentUser?.id ?? '',
      categoryId: defaultValues?.categoryId ?? (isExpenseEntry ? '' : undefined),
      feeTypeId: defaultValues?.feeTypeId ?? (isLegalFee ? '' : undefined),
      userType: defaultValues?.userType ?? currentUser?.roles?.[0]?.role
    }
  })

  const { handleSubmit, reset, clearErrors } = methods

  const [createManualTimeEntry] = useCreateManualEntryMutation({
    onError: error => {
      reset()
      return Alert.error(error.message)
    },
    onCompleted: async data => {
      reset()
      handleClose?.()
      if (data) Alert.success(`${titleize(pageEntryType)} entry created successfully`)
      refetch && (await refetch())
    }
  })

  const [updateTimeEntry] = useUpdateEntryMutation({
    onError: error => Alert.error(error.message),
    onCompleted: async data => {
      if (data) Alert.success(`${titleize(pageEntryType)} entry updated successfully`)
      refetch && (await refetch())
    }
  })

  const onSubmit: SubmitHandler<ManualTimeEntryForm> = async (data, event) => {
    const nativeEvent = event?.nativeEvent as SubmitEvent | undefined
    const isSaveEntry = (nativeEvent?.submitter as HTMLButtonElement)?.value === 'save'

    // `caseNumber` is a UI-only helper for the matter/rate lookup and is not
    // part of the entry inputs; everything else in `rest` is.
    const { duration, entryType, entryId, feeTypeId, categoryId, rate, caseNumber, ...rest } = data
    const userType = allUsers?.find(user => user.id === data.userId)?.roles?.[0]?.role as UserRole

    // Creating the matter note is a side effect of *turning the flag on*. On
    // edit, an entry that already had it set has had its note created, so only
    // a false -> true transition should produce another one.
    const shouldCreateMatterNote =
      !!data.sendNotesToMatterNotes && !!data.description && !!data.caseId && !!staffNoteTypeId

    const createMatterNote = async () => {
      if (!staffNoteTypeId) return
      await createNote({
        variables: {
          createNoteInput: {
            description: data.description,
            caseId: data.caseId,
            userId: data.userId,
            noteTypeId: staffNoteTypeId
          }
        }
      })
    }

    if (!isSaveEntry) {
      const input = {
        ...rest,
        rate: rate ?? 0,
        entryId: '',
        entryType: pageEntryType,
        userType,
        duration: undefined
      }
      startTimer(input)
    } else {
      const updatedDuration =
        !TIME_FORMAT_HH_MM_REGEX.test(duration ?? '') && TIME_FORMAT_HH_REGEX.test(duration ?? '')
          ? decimalHoursToHHMM(duration ?? '')
          : duration

      const input = {
        ...rest,
        rate: rate ?? 0,
        entryType: pageEntryType,
        userType,
        duration: isExpenseEntry || isLegalFee ? undefined : updatedDuration,
        feeTypeId: isLegalFee ? feeTypeId : undefined,
        categoryId: isExpenseEntry ? categoryId : undefined,
        date: dayjs(data.date).format('YYYY-MM-DDTHH:mm:ssZ'),
        caseId: isNonClientEntry ? crypto.randomUUID() : data.caseId, // TO HANDLE CASE ID FOR NON-CLIENT ENTRIES IN BE
        ...(isExpenseEntry && {
          qty: data.qty != null ? Number(data.qty) : undefined,
          unitPrice: data.unitPrice != null ? Number(data.unitPrice) : undefined
        })
      }

      if (title.includes('Edit')) {
        const result = await updateTimeEntry({
          variables: {
            input: {
              ...input,
              entryId
            }
          }
        })
        if (result.data && shouldCreateMatterNote && !defaultValues?.sendNotesToMatterNotes) await createMatterNote()
      } else {
        const result = await createManualTimeEntry({
          variables: { input }
        })
        if (result.data && shouldCreateMatterNote) await createMatterNote()
      }
    }

    handleClose?.()
  }

  const handleCancel = () => {
    clearErrors()
    reset()
    handleClose?.()
  }

  const getIcon = () => {
    if (isExpenseEntry) return <PaymentsOutlined />
    else if (isCostEntry) return createElement(InvestigatorLogo)
    else if (isTimeEntry || isLegalFee) return <AccessTime />
  }

  return (
    <>
      <Dialog
        open={isOpen ?? false}
        onClose={() => {}}
        disableEscapeKeyDown
        sx={{
          '& .MuiDialog-paper': {
            width: '600px',
            maxWidth: '90%'
          }
        }}
      >
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogTitle display={'flex'} alignItems={'center'} gap={2}>
              {getIcon()}
              {title}
            </DialogTitle>

            <DialogContent>
              <CommonEntryFields
                handleCancelDialog={handleCancel}
                buttonText="save"
                initialCase={initialCase}
                lockCase={lockCase}
              />
            </DialogContent>
          </form>
        </FormProvider>
      </Dialog>
    </>
  )
}

export default ManualEntry
